import React, { useState } from "react";
import { Row, Col, Form, Input, Card, Button, Select, Radio } from "antd";
import "./styles.css";
import { useSelector, useDispatch } from "react-redux";
import {
  setCurrentStep,
  setFormResponse,
  setRideDetails,
  setRideHistoryFlag,
  setSelectedTab,
  setTestId,
  setTripSummary,
} from "../../../../reducers/testRideSlice";
import { postApis } from "../../../Common/apis";
import { testRideFormSubmitApiUrl } from "../../../Common/endpoints";
import { toast } from "react-toastify";
import { numericInputValidator } from "../../../Common/Utils";
import { TEST_RIDE, UNITS } from "../../../Common/Constants";
import ConfirmTestId from "./ConfirmTestId";
import TimeRangeForm from "./TimeRangeDetails";
import dayjs from "dayjs";

const RideNotesForm = ({ timeRangeValidation }) => {
  const dispatch = useDispatch();
  const notesFormRef = React.useRef(null);
  const vehicleDetails = useSelector((state) => state.eol.vehicleDetails);
  const testRide = useSelector((state) => state.testRide);
  const rideDetails = testRide?.rideDetails;
  const formResponse = testRide?.formResponse;
  const authToken = localStorage.getItem("authToken");
  const [finishBtnLoader, setFinishBtnLoader] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [finalFormData, setFinalFormData] = useState({});

  const showModal = () => {
    setOpenModal(true);
  };
  const onBack = () => {
    const { climate, level, ...inputValues } =
      notesFormRef.current?.getFieldValue();
    dispatch(
      setRideDetails({
        ...rideDetails,
        ...inputValues,
        climateCondition: climate ? [climate, level] : [],
      })
    );
    dispatch(setCurrentStep(2));
  };

  const confirmFormSubmit = async () => {
    await postApis(
      testRideFormSubmitApiUrl,
      { ...finalFormData, userConfirm: true },
      authToken
    )
      .then((res) => {
        dispatch(setCurrentStep(0));
        dispatch(setRideDetails({}));
        dispatch(setSelectedTab("rideStatistics"));
        dispatch(setTripSummary({}));
        dispatch(setTestId(res?.data?.testId));
        dispatch(setRideHistoryFlag(res?.data?.testId));
        setFinishBtnLoader(false);
      })
      .catch((e) => {
        toast.error(e.response?.data?.message);
        setFinishBtnLoader(false);
      });
  };

  const onFinish = async (value) => {
    setFinishBtnLoader(true);

    const {
      startDate,
      startTime,
      endDate,
      endTime,
      roadCondition,
      traffic,
      climate,
      level,
      startAmbient,
      startBattery,
      startMCU,
      startMotor,
      startSOC,
      startVoltage,
      endAmbient,
      endMCU,
      endMotor,
      endBattery,
      endSOC,
      endVoltage,
      ...testRideValues
    } = { ...rideDetails, ...value };
    const startDateTime = dayjs(startDate)
      .set("hour", startTime.hour())
      .set("minute", startTime.minute())
      .set("second", 0);

    const endDateTime = dayjs(endDate)
      .set("hour", endTime.hour())
      .set("minute", endTime.minute())
      .set("second", 0);

    const errorList = timeRangeValidation(
      startDate,
      startTime,
      endDate,
      endTime
    );
    if (errorList) {
      notesFormRef?.current?.setFields(errorList);
    } else {
      const body = {
        formData: {
          imei: vehicleDetails.imeiNumber,
          startTime: Math.floor(startDateTime),
          stopTime: Math.floor(endDateTime),
          type: "TEST_RIDE",
          ...testRideValues,
          roadCondition: roadCondition || [],
          traffic: traffic || [],
          climateCondition: climate ? [climate, level] : [],
          rideStartVoltage: startVoltage,
          rideEndVoltage: endVoltage,
          vehicleDetailsBeforeRide: {
            batteryPercentage: startSOC,
            batteryVoltage: startVoltage,
            ambientTemperature: startAmbient,
            batteryTemperature: startBattery,
            mcuTemperature: startMCU,
            motorTemperature: startMotor,
          },
          vehicleDetailsAfterRide: {
            batteryPercentage: endSOC,
            batteryVoltage: endVoltage,
            ambientTemperature: endAmbient,
            batteryTemperature: endBattery,
            mcuTemperature: endMCU,
            motorTemperature: endMotor,
          },
        },
        userConfirm: false,
      };

      setFinalFormData(body);
      await postApis(testRideFormSubmitApiUrl, body, authToken)
        .then((res) => {
          dispatch(setFormResponse(res?.data));
          setFinishBtnLoader(false);
          if (res?.data?.testId === null) {
            showModal();
          } else {
            dispatch(setCurrentStep(0));
            dispatch(setRideDetails({}));
            dispatch(setSelectedTab("rideStatistics"));
            dispatch(setTripSummary({}));
            dispatch(setTestId(res?.data?.testId));
            dispatch(setRideHistoryFlag(res?.data?.testId));
          }
        })
        .catch((e) => {
          toast.error(e.response?.data?.message);
          setFinishBtnLoader(false);
        });
    }
  };

  return (
    <>
      <ConfirmTestId
        formResponse={formResponse}
        openModal={openModal}
        setOpenModal={setOpenModal}
        confirmFormSubmit={confirmFormSubmit}
      />
      <Form
        name="vehicleForm"
        layout="vertical"
        ref={notesFormRef}
        onFinish={onFinish}
        initialValues={{
          ...rideDetails,
          climate: rideDetails?.climateCondition?.[0] || null,
          level: rideDetails?.climateCondition?.[1] || "Moderate",
        }}
        validateMessages={{
          required: TEST_RIDE.FORM.REQUIRED_FIELDS.BLANK_ERROR,
          whitespace: TEST_RIDE.FORM.REQUIRED_FIELDS.BLANK_ERROR,
        }}
        autoComplete="off"
      >
        <Row gutter={16} className="mt1 ride-notes-form">
          <TimeRangeForm formRef={notesFormRef} />
          <Col xs={24} sm={24} md={24} lg={16} xl={16} xxl={18}>
            <Card bordered={false} className="input-box">
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="roadCondition"
                    label={TEST_RIDE.FORM.REQUIRED_FIELDS.ROAD_CONDITION}
                  >
                    <Select
                      allowClear
                      size="large"
                      mode="multiple"
                      title="roadCondition"
                      placeholder="Select a Road Condition"
                    >
                      <Select.Option value="CITY">CITY</Select.Option>
                      <Select.Option value="HIGHWAY">HIGHWAY</Select.Option>
                      <Select.Option value="VILLAGE">VILLAGE</Select.Option>
                      <Select.Option value="OFFROAD">OFF ROAD</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="traffic"
                    label={TEST_RIDE.FORM.REQUIRED_FIELDS.TRAFFIC}
                  >
                    <Select
                      allowClear
                      size="large"
                      mode="multiple"
                      placeholder="Select a Traffic"
                    >
                      <Select.Option value="NO">NO</Select.Option>
                      <Select.Option value="LOW">LOW</Select.Option>
                      <Select.Option value="MEDIUM">MEDIUM</Select.Option>
                      <Select.Option value="HIGH">HIGH</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={24}>
                  <Form.Item
                    label={TEST_RIDE.FORM.REQUIRED_FIELDS.CLIMATE_CONDITION}
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="climate" className="mb0">
                          <Select
                            allowClear
                            size="large"
                            placeholder="Select a Climate"
                          >
                            <Select.Option value="Sunny">Sunny</Select.Option>
                            <Select.Option value="Windy">Windy</Select.Option>
                            <Select.Option value="Rainy">Rainy</Select.Option>
                            <Select.Option value="Cold">Cold</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={10} xl={10} xxl={6}>
                        <Form.Item name="level" className="mb0">
                          <Radio.Group
                            optionType="button"
                            size="large"
                            className="display-block text-center"
                          >
                            <Radio.Button value="Moderate" className="w50">
                              Moderate
                            </Radio.Button>
                            <Radio.Button value="High" className="w50">
                              High
                            </Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={12}>
                  <Form.Item
                    name="flyoverCount"
                    label={TEST_RIDE.FORM.REQUIRED_FIELDS.FLYOVERS}
                    rules={[
                      {
                        message: "Enter a valid number",
                        validator: (_, value) => numericInputValidator(value),
                      },
                    ]}
                  >
                    <Input
                      placeholder="Number of flyovers"
                      maxLength={2}
                      allowClear
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={12}>
                  <Form.Item
                    name="distance"
                    label={
                      <span>
                        {TEST_RIDE.FORM.REQUIRED_FIELDS.DISTANCE}{" "}
                        <span className="f12 grey">({UNITS.KM})</span>
                      </span>
                    }
                    rules={[
                      {
                        message: "Enter a valid number",
                        validator: (_, value) =>
                          numericInputValidator(value, "decimalNegative"),
                      },
                    ]}
                  >
                    <Input
                      placeholder="KM"
                      maxLength={4}
                      allowClear
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Col span={24}>
          <Form.Item
            className="mt2"
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Row>
              <Button
                role="BackBtn"
                className="back-btn"
                htmlType="button"
                style={{ width: "80px" }}
                onClick={onBack}
              >
                Back
              </Button>
              <Button
                data-testid="submitBtn"
                type="primary"
                className="submit-btn"
                htmlType="submit"
                loading={finishBtnLoader}
                style={{ width: "100px", marginLeft: "2rem" }}
              >
                {TEST_RIDE.FORM.REQUIRED_FIELDS.SUBMIT}
              </Button>
            </Row>
          </Form.Item>
        </Col>
      </Form>
    </>
  );
};

export default RideNotesForm;
